import type { Currency } from "../../entities/ledger/types";

const scales: Record<Currency, number> = {
  GEL: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  TRY: 2,
  RUB: 2,
  JPY: 0,
  KWD: 3,
  UAH: 2,
  VND: 0,
  PLN: 2,
  CHF: 2,
};

export function decimal(minor: number, currency: Currency, signed = false): string {
  const scale = scales[currency];
  return new Intl.NumberFormat("en", {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
    signDisplay: signed ? "always" : "auto",
  }).format(minor / 10 ** scale);
}

export function money(minor: number, currency: Currency, signed = false): string {
  return `${decimal(minor, currency, signed)} ${currency}`;
}

export function localDateTimeValue(date = new Date()): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function greeting(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return "Good morning.";
  if (hour < 18) return "Good afternoon.";
  return "Good evening.";
}

// Bank statements arrive as an acquirer envelope, not as a name a person
// would write: "POS wallet - TARIEL LTD, 6.37 EUR, Sep 27 2026, MCC: 5411,
// VISA, 415479******4445". Everything after the merchant is already on the
// row (the amount and the date) or is of no use to a personal ledger (MCC,
// scheme, masked PAN). This returns the part worth reading; the full string
// stays available as the element's tooltip.
const CHANNEL_PREFIX = /^(?:pos|ecom|e-?commerce|atm|retail|online|contactless|card|internet)\b[^,\-–]*[-–]\s*/i;

export function payeeName(description: string): string {
  const trimmed = description.trim();
  if (trimmed === "") return "Transaction";
  const head = trimmed.split(",")[0].trim();
  const withoutChannel = head.replace(CHANNEL_PREFIX, "").trim();
  const name = withoutChannel === "" ? head : withoutChannel;
  return name === "" ? trimmed : name;
}

// True when the raw descriptor carries more than the name, so the row can
// offer the original without implying there is something to see when the
// description was already clean.
export function hasRawDescriptor(description: string): boolean {
  return payeeName(description) !== description.trim();
}

// decimal() is for reading: it groups thousands. A form input needs the plain
// value, because the grouped one ("3,000.00") is not what a parser accepts and
// putting it in a field is how a Save button ends up doing nothing.
export function amountValue(minor: number, currency: Currency): string {
  const scale = scales[currency];
  return (minor / 10 ** scale).toFixed(scale);
}

// parseAmount accepts what people and our own formatter actually write, and
// returns the canonical form, or null when it is not a number at all.
//
// Spaces are ignored. A comma is a thousands separator, except when it is the
// only separator and is followed by one or two digits, which no grouping ever
// is: "3,000.00" is three thousand, "3,50" is three and a half.
export function parseAmount(input: string, allowNegative = false): string | null {
  // U+00A0 and U+202F are the spaces number formatters emit; the apostrophe
  // is the Swiss thousands separator.
  const text = input.replace(/[\s\u00a0\u202f']/g, "");
  if (text === "") return null;
  const sign = text.startsWith("-") ? "-" : "";
  if (sign && !allowNegative) return null;
  let body = sign ? text.slice(1) : text;
  const commas = (body.match(/,/g) ?? []).length;
  const dots = (body.match(/\./g) ?? []).length;
  if (commas > 0 && dots === 0 && commas === 1 && /,\d{1,2}$/.test(body)) {
    body = body.replace(",", ".");
  } else {
    body = body.replace(/,/g, "");
  }
  if (!/^\d+(?:\.\d+)?$/.test(body)) return null;
  const [whole, fraction = ""] = body.split(".");
  if (fraction.length > 3) return null;
  return sign + (fraction === "" ? whole : `${whole}.${fraction}`);
}
